/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */
import { Route, Switch, useLocation } from 'react-router-dom';
import React, { ReactElement, useContext } from 'react';
import RequestAccessView from '../pages/RequestAccess/RequestAccessView';
import { Pages } from './Pages';
import CoursesOverviewView from '../pages/Courses/Overview/CoursesOverviewView';
import { PrivateRoute } from './PrivateRoute';
import { Redirect } from 'react-router';
import { observer } from 'mobx-react';
import { StoreProvider } from '../stores/StoreProvider';
import { StoreContext } from '../stores/storeContext';
import { AuthStore } from '../stores/application/AuthStore';
import StudentManagementView from '../pages/Courses/[id]/StudentManagement/StudentManagementView';
import NavBarView from '../components/Navbar/NavBarView';
import FooterView from '../components/Footer/FooterView';
import ForgotPasswordView from '../pages/ForgotPassword/ForgotPasswordView';
import ResetPasswordView from '../pages/ResetPassword/ResetPasswordView';
import ModuleEditingView from '../pages/Courses/[id]/ModuleEditing/ModuleEditingView';
import ChatView from '../pages/Courses/[id]/Chat/ChatView';
import EntriesReviewView from '../pages/Courses/[id]/EntriesReview/EntriesReviewView';
import PrivacyPolicyView from '../pages/LegalText/PrivacyPolicy/PrivacyPolicyView';
import ImprintView from '../pages/LegalText/Imprint/ImprintView';
import { Box, Container } from '@material-ui/core';
import RouterConfigStyles from './RouterConfigStyles';
import BasicNavBarView from '../components/Navbar/BasicNavBarView';
import CourseNavigationView from '../pages/Courses/[id]/CourseNavigation/CourseNavigationView';

const RouterConfig = observer((): ReactElement => {
	const storeProvider: StoreProvider = useContext(StoreContext);
	const authStore: AuthStore = storeProvider.authStore;
	const { pathname } = useLocation();
	const classes = RouterConfigStyles();
	const location = useLocation();

	function isNotOnLandingPage(): boolean {
		return pathname !== Pages.REQUEST_LOGIN && pathname !== Pages.RESET_PASSWORD && pathname !== Pages.FORGOT_PASSWORD;
	}

	function showCourseNav(): boolean {
		return authStore.hasAuthToken() && location.pathname.includes(`${Pages.COURSES_OVERVIEW}/`);
	}

	return (
		<>
			<Box className={classes.container}>
				{authStore.hasAuthToken() && <NavBarView />}
				<Switch>
					<Redirect from='/:url*(/+)' to={pathname.slice(0, -1)} />
					<Route exact path={Pages.REQUEST_LOGIN}>
						{authStore.hasAuthToken() ? <Redirect to={Pages.COURSES_OVERVIEW} /> : <RequestAccessView />}
					</Route>
					<Route exact path={Pages.FORGOT_PASSWORD}>
						{authStore.hasAuthToken() ? <Redirect to={Pages.COURSES_OVERVIEW} /> : <ForgotPasswordView />}
					</Route>
					<Route exact path={Pages.RESET_PASSWORD}>
						<ResetPasswordView />
					</Route>
					<Route exact path={Pages.IMPRINT}>
						{!authStore.hasAuthToken() && <BasicNavBarView />}
						<Box className={classes.minHeight}>
							<ImprintView />
						</Box>
					</Route>
					<Route exact path={Pages.PRIVACY_POLICY}>
						{!authStore.hasAuthToken() && <BasicNavBarView />}
						<Box className={classes.minHeight}>
							<PrivacyPolicyView />
						</Box>
					</Route>
					<Container className={classes.courseContainer}>
						{showCourseNav() && <CourseNavigationView />}
						<PrivateRoute exact path={Pages.COURSES_OVERVIEW} component={CoursesOverviewView} />
						<PrivateRoute
							path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.MODULES}
							component={ModuleEditingView}
							isLogin={authStore.hasAuthToken}
						/>
						<PrivateRoute
							path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.STUDENT_MANAGEMENT}
							component={StudentManagementView}
						/>
						<PrivateRoute
							path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.ENTRIES}
							component={EntriesReviewView}
							isLogin={authStore.hasAuthToken}
						/>
						<PrivateRoute path={Pages.COURSES_OVERVIEW + '/:courseId' + Pages.CHAT} component={ChatView} />
					</Container>
				</Switch>
			</Box>
			{isNotOnLandingPage() && <FooterView />}
		</>
	);
});

export default RouterConfig;
